import { useAppDispatch, useAppSelector } from '../hooks/reduxToolkit'
import { formatDistanceToNow, subMonths } from 'date-fns'
import { parseDateInTimeZone } from '../../lib/formatDateInTimeZone'
import { byCreatedAtDescending } from '../../lib/util/sort'
import { markNotificationAsRead, NotificationModel } from '../reduxToolkit/notificationsSlice'
import { useSentry } from '../providers/sentry'
import { useNavigate } from 'react-router'
import { useEffect, useMemo, useState } from 'react'
import { usePushNotifications } from '../providers/pushNotifications'
import { selectUnreadNotifications } from '../reduxToolkit/selectors/selectUnreadNotifications'
import { useClient } from '../providers/supabase'
import { getNotificationRecordTypeTable, NotificationRecordType, NotificationRecordTypeRow, NotificationRecordTypeTable, Tables } from '../../types/supabase'

import './notifications.scss'
import { useAsyncEffect } from '../hooks/useAsyncEffect'

export function Notifications() {
  const unreadNotifications = useAppSelector(selectUnreadNotifications)
  const originalUnreadNotifications = useMemo(() => [...unreadNotifications], [])

  const dispatch = useAppDispatch()
  const Sentry = useSentry()
  const pushNotifications = usePushNotifications()
  
  // When we first load the notifications page, we prompt the user to grant push notification permissions if they haven't already made a decision.
  useEffect(() => {
    if (originalUnreadNotifications.length <= 0) {
      return
    }

    const permissionState = pushNotifications.getPermissionState()
    if (permissionState === 'default') {
      // Don't await this, we just want to try to prompt the user
      pushNotifications.register()
        .catch((error) => {
          console.error('Error requesting notification permission:', error)
          Sentry.captureException(error)
        })
    }
  }, [originalUnreadNotifications.length <= 0, pushNotifications])
  
  const unreadCount = unreadNotifications.length
  
  // Show unread notifications, but also show any notifications that were unread when we first loaded the page but have since been read.
  const unreadNotificationIDs = unreadNotifications.map(n => n.id)
  const sortedNotifications = [
    ...unreadNotifications,
    ...originalUnreadNotifications.filter(n => !unreadNotificationIDs.includes(n.id))
  ].sort(byCreatedAtDescending)

  if (sortedNotifications.length === 0) {
    return <div className="row notifications">
      <div className="col-12">
        <h1 className="notifications__header">Notifications</h1>
        
        <div className="notifications__empty">
          <i className="material-icons-outlined">notifications_none</i>
          <p>No unread notifications</p>
        </div>
      </div>
    </div>
  }

  return <div className="row notifications">
    <div className="col-12">
      <div className="notifications__header-container">
        <h1 className="notifications__header">Notifications</h1>
        {unreadCount > 0 && (
          <div className="dropdown notifications__menu-button">
            <button 
              className="btn btn-link" 
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="material-icons-outlined">more_vert</i>
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              {unreadCount > 0 && (
                <li>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      const now = new Date().toISOString()
                      unreadNotifications.forEach(n => {
                        if (!n.read_at) {
                          dispatch(markNotificationAsRead({ id: n.id, read_at: now, updated_at: now }))
                        }
                      })
                    }}
                  >
                    Mark all as read
                  </button>
                </li>
              )}
            </ul>
          </div>
        )}
        
        <span className="notifications__unread-count text-muted">
          {unreadCount > 0 ?
            <i>{unreadCount} unread notifications</i> :
            <i>All notifications read</i>
          }
        </span>
      </div>
      
      <div className="notifications__list">
        {sortedNotifications.map(notification => {
          return <NotificationMessage key={notification.id} notification={notification} isUnread={unreadNotificationIDs.includes(notification.id)} />
        })}
      </div>
    </div>
  </div>
}

function NotificationMessage({notification, isUnread}: {notification: NotificationModel, isUnread: boolean}): React.ReactElement {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const supabase = useClient()
  const [associatedRecord, setAssociatedRecord] = useState<NotificationRecordTypeRow>()
  
  const {loading, refreshing, error} = useAsyncEffect(async () => {
      if (!notification.record_type || !notification.record_id) {
        return;
      }

        let result = notification.record_type ? await supabase
              .from(getNotificationRecordTypeTable(notification.record_type))
              .select('*')
              .eq('id', notification.record_id)
              .single() : null;
        
        if (result?.data) {
          setAssociatedRecord(result.data as NotificationRecordTypeRow)
        }
  }, [notification.record_type, notification.record_id, supabase])
  
  let href: string | null = null;
  if (associatedRecord && !loading && notification.record_type) {
    href = getNotificationRecordTypeHref(notification.record_type, associatedRecord);
  }

  return <div 
      key={notification.id} 
      className={`notification-item ${isUnread ? 'notification-item--unread' : ''} ${href ? 'notification-item--clickable' : ''}`}
  
      onClick={(e) => {
        e.preventDefault()
        const now = new Date().toISOString()
        if (isUnread) {
          dispatch(markNotificationAsRead({ id: notification.id, read_at: now, updated_at: now }))
        }
        if (href) {
          navigate(href)
        }
      }}
    >
      <div className="notification-item__content">
        <div className="notification-item__message">
          <h6 className="notification-item__title">{notification.title}</h6>
          <p className="notification-item__body">{notification.body}</p>
        </div>
        <div className="notification-item__time">
          {formatDistanceToNow(parseDateInTimeZone(notification.created_at), { addSuffix: true })}
        </div>
      </div>
    </div>
}


function getNotificationRecordTypeHref<T extends NotificationRecordType>(recordType: T, associatedRecord: NotificationRecordTypeRow<T>): string | null {
  switch (recordType) {
    case 'advance_request':
    case 'advance':
      if ('incident_id' in associatedRecord) {
        return `/incidents/${associatedRecord.incident_id}`
      }
      break
    case 'todo':
      return `/todos/${associatedRecord?.id}`
    case 'conversation':
      // For Alice conversations, navigate to the Alice conversation view
      if ('topic_type' in associatedRecord && associatedRecord.topic_type === 'alice-chat') {
        return `/alice/${associatedRecord.id}`
      }
      // For provider conversations, navigate directly to the conversation
      if ('topic_type' in associatedRecord && associatedRecord.topic_type === 'alice-provider-chat') {
        return `/conversations/${associatedRecord.id}`
      }
      // For itemized-bill conversations, navigate to the referenced expense
      if ('topic_ref_id' in associatedRecord && associatedRecord.topic_type === 'itemized-bill') {
        return `/expenses/${associatedRecord.topic_ref_id}`
      } else if ('provider_id' in associatedRecord && associatedRecord.provider_id) {
        // Navigate to the provider to view the conversation
        return `/providers/${associatedRecord.provider_id}`
      }
      break
    case 'expense':
      return `/expenses/${associatedRecord.id}`
  }
  return null
}
